import { useEffect, useState, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import { message } from "antd";
import { useTranslation } from "react-i18next";

const useGetRealTimeParkinglot = ({
  startChoose,
  projectId,
  parkingToken,
  bookingNo,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [tokenUsed, setTokenUsed] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const connectionRef = useRef(null);
  const currentRoomRef = useRef("");
  const { t } = useTranslation();

  const disconnect = async () => {
    const connection = connectionRef.current;
    if (connection) {
      try {
        await connection.stop();
        setConnected(false);
        console.log("SignalR connection stopped manually.");
      } catch (err) {
        console.warn("Error while stopping SignalR:", err);
      }
    }
  };

  const leaveRoom = async () => {
    const connection = connectionRef.current;
    if (connection && connection.state === "Connected") {
      try {
        console.log("Leaving room for projectId:", projectId);
        await connection.invoke("LeaveBlockProjectRoom", projectId);
      } catch (err) {
        console.warn("LeaveBlockProjectRoom failed:", err);
      }
    }
  };

  const joinRoom = async (connection) => {
    const room = `${projectId}`;
    currentRoomRef.current = room;
    setLoading(true);
    try {
      await connection.invoke("JoinBlockProjectRoom", projectId);
      setError(false);
      setErrorMessage(null);
    } catch (err) {
      message.error(t("cart.token_invalid") + ": " + err?.message);
      setError(true);
      setLoading(false);
    }
  };

  const connect = async () => {
    if (!projectId || !parkingToken || !bookingNo || !startChoose) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(
        `${
          process.env.REACT_APP_API
        }signalr-shopping-cart?parking_token=${parkingToken.trim()}&booking_no=${bookingNo.trim()}`
      )
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    connectionRef.current = connection;
    connection.serverTimeoutInMilliseconds = 30000;
    connection.keepAliveIntervalInMilliseconds = 5000;

    connection.on("JoinRoomConfirmation", (response) => {
      if (response.connected === false) {
        setError(true);
        setData([]);
        setConnected(false);
        setLoading(false);
        return;
      }
      setData(response);
      setConnected(true);
      setLoading(false);
    });

    connection.on("ReceiveShoppingCartUpdate", (response) => {
      if (response.connected === false) return;
      setData(response);
      setConnected(true);
      setLoading(false);
    });

    connection.on("LeaveRoomConfirmation", (msg) => {
      console.log("Left room:", msg);
    });

    connection.on("ForceDisconnect", () => {
      setErrorMessage("cart.token_already_used");
      setTokenUsed(true);
      setTimeout(() => {
        setConnected(false);
        leaveRoom();
        connectionRef.current?.stop();
      }, [100]);
    });

    connection.on("ProjectEndEvent", () => {
      setErrorMessage("cart.project_end_Event");
      leaveRoom();
      setConnected(false);
      connectionRef.current?.stop();
    });

    connection.onclose(async (error) => {
      setConnected(false);
      setError(true);

      // if (error) {
      //     console.error("SignalR disconnected with error:", error.message);
      //     console.log(tokenUsed);

      //     setTimeout(() => {
      //         tokenUsed ? setErrorMessage(t("cart.token_already_used")) : setErrorMessage(t("cart.signalr_disconnected"));
      //     }, [100])
      // } else {
      //     console.log(tokenUsed);
      //     console.log("SignalR disconnected gracefully.");
      //     setTimeout(() => {
      //         tokenUsed ? setErrorMessage(t("cart.token_already_used")) : setErrorMessage(t("cart.signalr_disconnected"));
      //     }, [100])
      // }
    });

    try {
      await connection.start();
      console.log("SignalR connected");
      joinRoom(connection);
    } catch (err) {
      console.error("SignalR connection error:", err);
      setLoading(false);
    }
  };

  // const reconnect = async () => {
  //     try {
  //         await disconnect(); // đảm bảo dừng kết nối cũ
  //         connectionRef.current = null; // ✅ reset lại hẳn connection
  //     } catch (err) {
  //         console.warn("Error during disconnect in reconnect:", err);
  //     }

  //     setTimeout(() => {
  //         connect(); // đợi một chút rồi connect lại
  //     }, 100); // tránh race condition
  // };

  // 👇 Lắng nghe online/offline
  useEffect(() => {
    const handleOnline = () => {
      console.log("Back online");
      setError(false);
      setErrorMessage(null);
      if (!connected) {
        connect(); // ✅ Tái kết nối nếu chưa kết nối
      }
    };

    const handleOffline = () => {
      console.log("Disconnected from network");
      disconnect();
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  useEffect(() => {
    connect();

    return () => {
      leaveRoom();
      connectionRef.current?.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, parkingToken, bookingNo, startChoose]);

  return {
    data,
    loading,
    connected,
    error,
    errorMessage,
    disconnect,
  };
};

export default useGetRealTimeParkinglot;
